import WaCard from "@awesome.me/webawesome-pro/dist/react/card/index.js";
import WaDivider from "@awesome.me/webawesome-pro/dist/react/divider/index.js";
import WaFormatNumber from "@awesome.me/webawesome-pro/dist/react/format-number/index.js";
import WaIcon from "@awesome.me/webawesome-pro/dist/react/icon/index.js";
import WaTooltip from "@awesome.me/webawesome-pro/dist/react/tooltip/index.js";
import React from "react";

import circleInfoIcon from "../assets/icons/circle-info.svg";
import type { EstimateResponse } from "../types/journey";
import { duration, minutes, signedDelay } from "../utils/time";

const SpacedDivider: React.FC = () => (
  <div className="wa-stack wa-gap-xs" style={{ paddingBlock: 12 }}>
    <WaDivider />
  </div>
);

const shortPlace = (s?: string | null) => (s ? s.split(",")[0].trim() : "");

export const PricingBreakdown: React.FC<{
  estimate: EstimateResponse | null;
  originLabel?: string | null;
  destinationLabel?: string | null;
  breakdownOnly?: boolean;
  hideBreakdown?: boolean;

  /**
   * Optional: makes the "Journey" header a tap target (useful on mobile)
   * Parent can swap back to the journey input screen.
   */
  onEditJourney?: () => void;
}> = ({
  estimate,
  originLabel,
  destinationLabel,
  breakdownOnly = false,
  hideBreakdown = false,
  onEditJourney,
}) => {
  if (!estimate) {
    return (
      <WaCard className="w-full wa-p-l wa-stack wa-gap-m">
        <div className="wa-color-neutral-600 wa-body-m" style={{ textAlign: "center" }}>
          Enter an origin + destination to see pricing.
        </div>
      </WaCard>
    );
  }

  const originShort = shortPlace(originLabel);
  const destShort = shortPlace(destinationLabel);
  const hasJourneyLabels = Boolean(originShort || destShort);

  const { currency } = estimate.pricing;

  const distanceMiles = estimate.route.distanceMiles;
  const passengerCount = estimate.passengerCount ?? 1;
  const typicalSeconds = estimate.route.typicalDurationSeconds;
  const journeySeconds = estimate.route.journeyDurationSeconds;

  const trafficDelaySeconds = estimate.route.trafficDelaySeconds ?? 0;
  const chargeableWaitingSeconds = estimate.route.chargeableWaitingSeconds ?? 0;

  const hasTrafficDelay = trafficDelaySeconds > 0;
  const hasChargeableWaiting = chargeableWaitingSeconds > 0;
  const isLeaveNow = hasTrafficDelay || hasChargeableWaiting; // proxy for leaveNow

  const statCols = "repeat(2, minmax(0, 1fr))";

  const Money: React.FC<{ value: number }> = ({ value }) => (
    <WaFormatNumber
      value={value}
      type="currency"
      currency={currency}
      minimumFractionDigits={2}
      maximumFractionDigits={2}
    />
  );

  const firstMileCharge = estimate.pricing.firstMileCharge ?? 0;
  const remainingMilesTotal = estimate.pricing.remainingMilesTotal ?? 0;
  const firstMileFare = estimate.pricing.firstMileFare ?? 0;

  const waitingCharge = estimate.pricing.waitingCharge ?? 0;

  const waitingIncrementSeconds = (estimate.pricing as any).waitingIncrementSeconds as
    | number
    | undefined;
  const waitingIncrementAmount = (estimate.pricing as any).waitingIncrementAmount as
    | number
    | undefined;
  const waitingIncrements = (estimate.pricing as any).waitingIncrements as number | undefined;

  const firstMileHint = `(${formatMoney(firstMileFare, currency)} incl. base fare)`;

  const trafficChargeHint =
    waitingIncrementSeconds && waitingIncrementAmount != null && waitingIncrements != null
      ? `(${waitingIncrements} × ${duration(waitingIncrementSeconds)} @ ${formatMoney(
          waitingIncrementAmount,
          currency,
        )})`
      : undefined;

  const milesAfterFirst = Math.max(0, distanceMiles - 1);
  const additionalMilesHint =
    milesAfterFirst > 0
      ? `(${milesAfterFirst.toFixed(1)} mi × £${estimate.pricing.perMileAfterFirstRate.toFixed(
          2,
        )}/mi)`
      : undefined;

  return (
    <div
      className="wa-stack wa-gap-m wa-width-full"
      style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}
    >
      {/* Journey summary (tap to edit) */}
      {!breakdownOnly && (
        <>
          {hasJourneyLabels && (
            <>
              <button
                type="button"
                onClick={onEditJourney}
                disabled={!onEditJourney}
                aria-label={`Edit journey from ${originShort || "Origin"} to ${destShort || "Destination"}`}
                style={{
                  appearance: "none",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  width: "100%",
                  cursor: onEditJourney ? "pointer" : "default",
                  borderRadius: 12,
                }}
              >
                <div
                  className="wa-stack wa-gap-xs"
                  style={{
                    textAlign: "center",
                    padding: onEditJourney ? "6px 0" : 0,
                    borderRadius: 12,
                  }}
                >
                  {/* Section label + Edit affordance */}
                  <div
                    className="wa-cluster wa-gap-s"
                    style={{
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span className="wa-caption-s wa-color-neutral-600" style={{ fontWeight: 700 }}>
                      Journey
                    </span>

                    {onEditJourney && (
                      <span
                        className="wa-caption-s wa-color-neutral-600"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          opacity: 0.85,
                        }}
                      >
                        <span>•</span>
                        <span style={{ textDecoration: "underline" }}>Edit</span>
                        <span aria-hidden style={{ display: "inline-flex" }}>
                          <WaIcon name="chevron-right" />
                        </span>
                      </span>
                    )}
                  </div>

                  {/* Route title */}
                  <div className="pricing-breakdown__journey">
                    <span className="pricing-breakdown__journeyText">
                      {originShort || "Origin"}
                      <span className="pricing-breakdown__journeyArrow" aria-hidden>
                        <WaIcon name="arrow-right" />
                      </span>
                      {destShort || "Destination"}
                    </span>
                  </div>
                </div>
              </button>

              <SpacedDivider />
            </>
          )}

          {/* Header */}
          <div className="wa-stack wa-gap-s">
            <div
              className="wa-cluster wa-align-center wa-gap-s pricing-breakdown__header"
              style={{ justifyContent: "space-between", flexWrap: "wrap" }}
            >
              <span
                id="tariff-chip"
                className="wa-body-s tariff-chip"
                style={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(15,23,42,0.12)",
                  background: "rgba(15,23,42,0.03)",
                  color: "rgba(15,23,42,0.65)",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Tariff <strong>{estimate.pricing.tariffCode}</strong>
                <span>•</span>
                <span>{estimate.pricing.tariffName}</span>
                <span
                  aria-hidden
                  style={{ display: "inline-flex", alignItems: "center", opacity: 0.6 }}
                >
                  <img
                    src={circleInfoIcon}
                    alt=""
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                      transform: "translateY(0.29em)",
                    }}
                  />
                </span>
                <WaTooltip for="tariff-chip" placement="top">
                  {estimate.pricing.tariffInfoCopy}
                </WaTooltip>
              </span>

              <div
                className="wa-cluster wa-gap-xs"
                style={{
                  alignItems: "center",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: "rgba(34,197,94,0.10)",
                  border: "1px solid rgba(34,197,94,0.32)",
                  flexShrink: 0,
                }}
              >
                <span className="wa-bodym wa-color-neutral-900" style={{ fontWeight: 700 }}>
                  Fare
                </span>

                <span
                  className="wa-heading-l"
                  style={{
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.01em",
                  }}
                >
                  <Money value={estimate.pricing.total} />
                </span>
              </div>
            </div>
          </div>

          <SpacedDivider />

          {/* Journey stats */}
          <div
            className="wa-grid wa-gap-s pricing-breakdown__stats"
            style={{ gridTemplateColumns: statCols }}
          >
            <Stat label="Distance" value={`${distanceMiles.toFixed(1)} mi`} />
            <Stat
              label="Passengers"
              value={`${passengerCount} ${passengerCount === 1 ? "person" : "people"}`}
            />
            {isLeaveNow && <Stat label="Usual journey time" value={minutes(typicalSeconds)} />}
            {hasTrafficDelay && (
              <Stat label="Estimated traffic" value={signedDelay(trafficDelaySeconds)} />
            )}
            <Stat label="Estimated journey time" value={minutes(journeySeconds)} />
            {hasChargeableWaiting && (
              <Stat label="Billed traffic delay" value={duration(chargeableWaitingSeconds)} />
            )}
          </div>
          {!hideBreakdown && <SpacedDivider />}
        </>
      )}
      {!hideBreakdown && (
        <>
          {/* Fare breakdown */}
          <div className="wa-stack wa-gap-s">
            {!breakdownOnly && (
              <div className="wa-heading-m wa-text-color-neutral-600">Fare breakdown</div>
            )}

            <div className="wa-stack wa-gap-0">
              <Row label="Base fare" value={<Money value={estimate.pricing.baseFare} />} />

              <SpacedDivider />
              <Row
                label="First mile"
                hint={firstMileHint}
                value={<Money value={firstMileCharge} />}
              />

              <SpacedDivider />
              <Row
                label="Additional miles"
                value={<Money value={remainingMilesTotal} />}
                hint={additionalMilesHint}
              />

              {hasChargeableWaiting && (
                <>
                  <SpacedDivider />
                  <Row
                    label="Traffic delay charge"
                    value={<Money value={waitingCharge} />}
                    hint={trafficChargeHint}
                  />
                </>
              )}

              <SpacedDivider />

              {/* Total */}
              <div
                className="wa-split "
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  background: "rgba(34,197,94,0.10)",
                  border: "1px solid rgba(34,197,94,0.32)",
                  alignItems: "baseline",
                }}
              >
                <span className="wa-body-s wa-color-neutral-900" style={{ fontWeight: 800 }}>
                  Estimated Total
                </span>
                <span
                  className="wa-body-s"
                  style={{ fontWeight: 900, fontVariantNumeric: "tabular-nums" }}
                >
                  <Money value={estimate.pricing.total} />
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    className="wa-stack wa-gap-3xs"
    style={{
      border: "1px solid rgba(15,23,42,0.08)",
      background: "rgba(15,23,42,0.015)",
      borderRadius: 14,
      padding: "10px 12px",
      minWidth: 0,
    }}
  >
    <div className="wa-caption-s wa-color-neutral-600">{label}</div>
    <div className="wa-body-s" style={{ fontWeight: 750, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
  </div>
);

const Row: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="wa-split wa-width-full" style={{ alignItems: "baseline", gap: 12 }}>
    <div className="wa-stack wa-gap-3xs" style={{ minWidth: 0 }}>
      <span className="wa-body-s wa-color-neutral-700">{label}</span>
      {hint && (
        <span className="wa-caption-s wa-color-neutral-500" style={{ lineHeight: 1.2 }}>
          {hint}
        </span>
      )}
    </div>

    <span className="wa-body-s" style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </span>
  </div>
);

function formatMoney(value: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // fallback
    return `${value.toFixed(2)} ${currency}`;
  }
}
